import { usePreReleaseFeatures } from 'hooks/usePreReleaseFeatures';
import { CUSTOM_OBJECTS } from 'queries/query-keys';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import CustomObjectsService from 'services/CustomObjectsService';
import { FORCE_ALL_RECORDS_SIZE } from 'services/helpers';
import {
  DEFAULT_ITEM_IDS,
  DEFAULT_MY_ACCOUNT,
  DEFAULT_TOOLBAR_ITEMS,
  getTranslationKeyMapping,
  PREVIEW_FLAG_ITEM_IDS,
} from '../constants';
import useToolbarAccessFiltering from './useToolbarAccessFiltering';
import { PluginContext } from 'ts-components/Plugins/context';
import { iconOptions } from 'components/MultiColumnBuilder/components/IconSelector';
import { useSelector } from 'react-redux';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { useDefaultModelQuery } from 'queries/models/custom-objects';

const doesSearchMatch = (item, term, translatedKeys) => {
  if (!term) {
    return true;
  }

  if (item.isDynamic) {
    return true;
  }

  if (item.label.toLowerCase().includes(term.toLowerCase())) {
    return true;
  }

  const translatedLabel = translatedKeys[item.id];

  return translatedLabel?.toLowerCase().includes(term.toLowerCase()) ?? false;
};

const useLeftColumn = (rightColumnItemsMap = {}, searchTerm) => {
  const prereleaseFeatures = usePreReleaseFeatures();
  const filterToolbar = useToolbarAccessFiltering();
  const businessClientObject = useSelector(getBusinessClientObject);
  const { data: clientObject, isLoading: isLoadingClientObject } =
    useDefaultModelQuery(businessClientObject.id);

  const itemEnabledByFeature = useCallback(
    (item) => {
      if (prereleaseFeatures) {
        return true;
      }

      return !PREVIEW_FLAG_ITEM_IDS.includes(item.id);
    },
    [prereleaseFeatures]
  );

  const { toolbarItems } = useContext(PluginContext);

  const iconOptionsLookup = useMemo(
    () =>
      iconOptions.reduce((acc, el) => {
        acc[el.value] = el;
        return acc;
      }, {}),
    []
  );

  const { defaultItemsMap, defaultItemsKeys } = useMemo(() => {
    const resDefault = DEFAULT_TOOLBAR_ITEMS.reduce((acc, curr) => {
      const children =
        curr.children?.reduce((childAcc, childCurr) => {
          const icon =
            childCurr.id === DEFAULT_ITEM_IDS.CONTACTS
              ? iconOptionsLookup[clientObject?.defaultIcon] || childCurr.icon
              : childCurr.icon;
          return {
            ...childAcc,
            [childCurr.id]: { ...childCurr, icon },
          };
        }, {}) ?? {};
      return {
        ...acc,
        [curr.id]: curr,
        ...children,
      };
    }, {});

    const resMyAccount = DEFAULT_MY_ACCOUNT.children.reduce((acc, curr) => {
      return {
        ...acc,
        [curr.id]: curr,
      };
    }, {});

    const resPluginToolbarItems = toolbarItems.reduce((acc, curr) => {
      const item = {
        ...curr,
        id: `${curr.plugin_api_name}-${curr.api_name}`,
        isDefault: false,
        isLocked: false,
        isPluginItem: true,
        isDynamic: false,
        type: 'item',
        widthPercent: 50,
      };

      return {
        ...acc,
        [item.id]: item,
      };
    }, {});

    const res = {
      ...resDefault,
      ...resMyAccount,
      ...resPluginToolbarItems,
    };

    return { defaultItemsMap: res, defaultItemsKeys: Object.keys(res) };
  }, [toolbarItems, clientObject, iconOptionsLookup]);

  const leftHandDefaultItems = useMemo(() => {
    return defaultItemsKeys
      .filter((x) => !rightColumnItemsMap[x])
      .map((x) => defaultItemsMap[x])
      .filter((i) => i.type === 'item');
  }, [rightColumnItemsMap, defaultItemsKeys, defaultItemsMap]);

  const { t } = useTranslation();

  const { data: customObjects, isLoading } = useQuery({
    queryKey: [...CUSTOM_OBJECTS.LIST, FORCE_ALL_RECORDS_SIZE],
    queryFn: () => {
      return CustomObjectsService.getCustomObjectList({
        size: FORCE_ALL_RECORDS_SIZE,
      });
    },
    refetchOnWindowFocus: false,
  });

  const customObjectOptions = useMemo(() => {
    return (
      customObjects?.results?.map((object) => {
        return {
          label: object.objectName,
          type: 'item',
          isDefault: false,
          isCustomObject: true,
          id: object.id,
          icon: iconOptionsLookup[object.defaultIcon] || 'bars-light',
          color: object.defaultColor,
        };
      }) ?? []
    );
  }, [customObjects, iconOptionsLookup]);

  const leftColumnOptions = useMemo(() => {
    const results = [
      {
        label: t('New Category (Drag to Create)'),
        isDefault: false,
        isDynamic: true,
        id: 'new_category',
        isLocked: false,
      },
      {
        label: t('New Custom URL (Drag to Create)'),
        icon: 'toolbar-external-link',
        isDefault: false,
        isDynamic: true,
        id: 'new_custom_url',
        isLocked: false,
        hasPoppedUp: false,
        hasPopup: true,
      },
      {
        activityId: null,
        hasPopup: true,
        hasPoppedUp: false,
        icon: {
          label: 'pen-to-square',
          value: 'pen-to-square',
          className: 'fa-light fa-pen-to-square',
        },
        id: 'new_log_activity',
        isDefault: false,
        isDynamic: true,
        isLocked: false,
        label: t('Log Activity (Drag to Create)'),
      },
      {
        label: t('Recent Records (Drag to Create)'),
        isDefault: false,
        isDynamic: true,
        id: 'new_recent_records',
        isLocked: false,
        hasPoppedUp: false,
        hasPopup: true,
        type: 'category',
        children: [],
        dynamicallyPopulated: true,
      },
      ...customObjectOptions,
      defaultItemsMap[DEFAULT_ITEM_IDS.CONTACTS],
    ];

    return results;
  }, [t, customObjectOptions, defaultItemsMap]);

  const [leftColumnState, setLeftColumState] = useState(leftColumnOptions);

  const leftColumnStateWithDefaults = useMemo(() => {
    const results = [...leftColumnState];

    const leftColumnMap = results.reduce((acc, curr) => {
      return {
        ...acc,
        [curr.id]: curr,
      };
    }, {});

    leftHandDefaultItems.forEach((item) => {
      if (!leftColumnMap[item.id]) {
        results.push({
          ...item,
          widthPercent: 100,
        });
      }
    });

    return results;
  }, [leftColumnState, leftHandDefaultItems]);

  const itemsOrdering = useMemo(
    () => leftColumnStateWithDefaults.map(({ id }) => id),
    [leftColumnStateWithDefaults]
  );

  useEffect(() => {
    setLeftColumState(leftColumnOptions);
  }, [leftColumnOptions]);

  const filteredLeftColumnState = useMemo(() => {
    const translatedKeys = getTranslationKeyMapping(t);

    return filterToolbar(
      leftColumnStateWithDefaults.filter((s) => {
        return (
          ((s.isDefault && s.id !== DEFAULT_ITEM_IDS.CONTACTS) ||
            !rightColumnItemsMap[s.id]) &&
          doesSearchMatch(s, searchTerm, translatedKeys) &&
          itemEnabledByFeature(s)
        );
      })
    );
  }, [
    leftColumnStateWithDefaults,
    rightColumnItemsMap,
    searchTerm,
    itemEnabledByFeature,
    filterToolbar,
    t,
  ]);

  return {
    items: filteredLeftColumnState,
    setItems: setLeftColumState,
    loading: isLoading || isLoadingClientObject,
    itemsOrdering,
  };
};

export default useLeftColumn;
